import React from 'react';
import { View, StyleSheet, Text, TouchableOpacity, Platform } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';
import { stripInstructionHtml } from '../../utils/stripInstructionHtml';
import { stripStepNumbers } from '../../utils/stepNumbering';
import { AdjustedText } from '../shared/AdjustedText';
import { AdjustedChip } from '../shared/AdjustedChip';
import type { RecipeAdjustment } from '../../hooks/useRecipeAdjustment';

const isWeb = Platform.OS === 'web';

export interface CookingStep {
  number: number;
  step: string;
  tip?: string;
  ingredients?: string[];
}

export interface CookingStepSection {
  name?: string;  // Section header (e.g., "Aubergines", "Sauce tomate")
  steps: CookingStep[];
}

interface RecipeCookingStepsProps {
  steps: CookingStep[];
  sections?: CookingStepSection[];  // Optional sectioned format with headers
  onWatch?: () => void;
  /**
   * Same adjustment the ingredient list uses. Step prose carries quantities
   * too ("add 2 cups of stock"), so it has to follow the toggle or the two
   * halves of the page contradict each other.
   */
  adjustment: RecipeAdjustment;
  baseServings: number;
  servings: number;
  onResetServings?: () => void;
}

export const RecipeCookingSteps: React.FC<RecipeCookingStepsProps> = ({
  steps,
  sections,
  onWatch,
  adjustment,
  baseServings,
  servings,
  onResetServings,
}) => {
  const { t } = useTranslation();
  // Format step number with leading zero
  const formatStepNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  // Check if we have valid sections with names (at least one section has a name)
  const hasSectionHeaders = sections && sections.length > 0 && sections.some(s => s.name);

  // The publisher's own step numbers come off the text ("1. ", "2 ", "1Dans"),
  // or each row reads its number twice (#794). Whether a bare "2 " is a step
  // number depends on the whole list, so the rendered list is cleaned at once.
  const rendered = hasSectionHeaders ? sections!.flatMap(s => s.steps) : steps;
  const cleanedTexts = new Map<CookingStep, string>();
  stripStepNumbers(rendered.map(s => (s.step ? stripInstructionHtml(s.step) : '')))
    .texts.forEach((text, i) => cleanedTexts.set(rendered[i], text.trim()));
  const cleanStepText = (step: CookingStep): string => cleanedTexts.get(step) ?? '';

  // Render a single step row
  const renderStepRow = (step: CookingStep, index: number) => (
    <View key={`step-${step.number}-${index}`} testID="recipe-step" style={styles.stepItem}>
      <View style={styles.stepTextContainer}>
        <Text style={styles.stepNumber}>{formatStepNumber(step.number)}</Text>
        <View style={styles.stepContent}>
          <AdjustedText
            text={cleanStepText(step)}
            options={adjustment.options}
            style={styles.stepText}
          />
          {step.ingredients && step.ingredients.length > 0 && (
            <View style={styles.ingredientChips}>
              {step.ingredients.map((ing, idx) => (
                <View key={idx} style={styles.ingredientChip}>
                  <Text style={styles.ingredientChipText}>{ing}</Text>
                </View>
              ))}
            </View>
          )}
          {!!step.tip && (
            <View style={styles.tipContainer}>
              <View style={styles.tipBorder} />
              <Text style={styles.tipText}>Tip: {step.tip}</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Section Header */}
      <View style={styles.header}>
        <Text style={styles.sectionTitle}>{t('recipeDetail.steps')}</Text>
        {/* Repeated here, not just above the ingredients: someone scrolled to
            the steps must not have to scroll back to learn the quantities in
            front of them are not the published ones. */}
        <AdjustedChip
          adjustment={adjustment}
          baseServings={baseServings}
          servings={servings}
          onResetServings={onResetServings}
          style={styles.headerChip}
        />
        {onWatch && (
          <TouchableOpacity
            style={styles.watchButton}
            onPress={onWatch}
            activeOpacity={0.7}
          >
            <Text style={styles.watchText}>{t('recipeDetail.watch')}</Text>
            <Feather name="play" size={14} color={Paprika.background} />
          </TouchableOpacity>
        )}
      </View>

      {/* Steps List - render with section headers if available */}
      <View style={styles.stepsList}>
        {hasSectionHeaders ? (
          // Render with section headers
          sections!.map((section, sectionIdx) => (
            <View key={`section-${sectionIdx}`}>
              {!!section.name && (
                <View style={styles.sectionHeaderContainer}>
                  <Text testID="recipe-step-section" style={styles.sectionHeader}>{section.name}</Text>
                  <View style={styles.sectionHeaderUnderline} />
                </View>
              )}
              {section.steps.map((step, stepIdx) => renderStepRow(step, stepIdx))}
            </View>
          ))
        ) : (
          // Render flat list (backward compatibility)
          steps.map((step, index) => renderStepRow(step, index))
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: PaprikaSpacing.lg,
    paddingBottom: 100, // Space for bottom bar
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.lg,
  },
  sectionTitle: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.textPrimary,
  },
  headerChip: {
    marginRight: 'auto',
    marginLeft: PaprikaSpacing.md,
  },
  sectionHeaderContainer: {
    marginTop: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.md,
  },
  sectionHeader: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    paddingBottom: PaprikaSpacing.xs,
  },
  sectionHeaderUnderline: {
    width: 40,
    height: 2,
    backgroundColor: Paprika.accent,
    borderRadius: 1,
  },
  watchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.xs,
    paddingHorizontal: PaprikaSpacing.md,
    backgroundColor: Paprika.textPrimary,
    borderRadius: PaprikaRadius.full,
    gap: 6,
  },
  watchText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.background,
  },
  stepsList: {
    paddingHorizontal: PaprikaSpacing.lg,
    maxWidth: 900,
    width: '100%',
    alignSelf: 'flex-start',
  },
  stepItem: {
    marginBottom: 22,
  },
  stepTextContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.md,
  },
  stepNumber: {
    fontFamily: 'Newsreader-Italic',
    fontSize: isWeb ? 44 : 30,
    lineHeight: isWeb ? 48 : 34,
    color: '#e6d8c8',
    width: isWeb ? 64 : 44,
    marginTop: isWeb ? -6 : -2,
    textAlign: 'left',
  },
  stepContent: {
    flex: 1,
    paddingTop: isWeb ? 8 : 4,
  },
  stepText: {
    fontFamily: 'Inter-Regular',
    fontSize: 15,
    lineHeight: 24,
    color: Paprika.onSurfaceVariant,
    marginBottom: PaprikaSpacing.sm,
  },
  ingredientChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.xs,
    marginBottom: PaprikaSpacing.sm,
  },
  ingredientChip: {
    paddingVertical: 6,
    paddingHorizontal: PaprikaSpacing.sm,
    backgroundColor: Paprika.secondaryContainer,
    borderRadius: PaprikaRadius.sm,
  },
  ingredientChipText: {
    ...PaprikaTypography.caption,
    color: Paprika.onSecondaryContainer,
  },
  tipContainer: {
    flexDirection: 'row',
    marginTop: PaprikaSpacing.xs,
  },
  tipBorder: {
    width: 3,
    backgroundColor: Paprika.accent,
    borderRadius: 1.5,
    marginRight: PaprikaSpacing.sm,
  },
  tipText: {
    flex: 1,
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    fontStyle: 'italic',
    lineHeight: 20,
  },
});

export default RecipeCookingSteps;
